import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { useAuth } from "@/hooks/use-auth";
import { safeSessionStorageGet, safeSessionStorageSet } from "@/lib/safe-browser";
import { getMyEffectiveEntitlement } from "@/lib/entitlement.functions";
import type { EntitlementSource } from "@/lib/effective-tier";
import type { PlanTier } from "@/lib/plan-tier";
import { useDemoMode } from "@/lib/demo-mode";

export type SubscriptionRow = {
  id: string;
  status: string;
  price_id: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  cancel_at?: string | null;
  environment: string;
};

type PremiumCache = {
  subscription: SubscriptionRow | null;
  tier: PlanTier;
  source: EntitlementSource;
  savedAt: number;
};

// Stale-while-revalidate: always show cached value instantly, refetch in background.
const PREMIUM_UI_LOADING_MS = 1200;
const PREMIUM_NETWORK_TIMEOUT_MS = 12_000;
// This cache is only an optimistic UI snapshot. Premium operations are still
// authorised server-side, so hiding a known plan while the network wakes up
// adds delay without adding security. Revalidation below remains immediate.

const premiumMemoryCache = new Map<string, PremiumCache>();
const premiumInflight = new Map<string, Promise<void>>();
const premiumListeners = new Map<string, Set<(cache: PremiumCache) => void>>();

function getPremiumCacheKey(userId: string, environment: string) {
  return `routify:premium:${environment}:${userId}`;
}

function readPremiumCache(userId: string, environment: string): PremiumCache | null {
  const key = getPremiumCacheKey(userId, environment);
  const memory = premiumMemoryCache.get(key);
  if (memory) return memory;
  if (typeof window === "undefined") return null;
  try {
    const raw = safeSessionStorageGet(key) ?? (typeof localStorage !== "undefined" ? localStorage.getItem(key) : null);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PremiumCache;
    if (!parsed?.savedAt || !parsed.tier || !parsed.source) return null;
    premiumMemoryCache.set(key, parsed);
    return parsed;
  } catch {
    return null;
  }
}

function writePremiumCache(userId: string, environment: string, value: Omit<PremiumCache, "savedAt">) {
  const key = getPremiumCacheKey(userId, environment);
  const next = { ...value, savedAt: Date.now() } satisfies PremiumCache;
  premiumMemoryCache.set(key, next);
  premiumListeners.get(key)?.forEach((listener) => listener(next));
  if (typeof window === "undefined") return;
  try {
    const serialized = JSON.stringify(next);
    safeSessionStorageSet(key, serialized);
    if (typeof localStorage !== "undefined") localStorage.setItem(key, serialized);
  } catch {
    // Cache is only a speed-up; ignore storage failures.
  }
}

export function setPremiumSubscriptionCache(userId: string, environment: string, subscription: SubscriptionRow) {
  writePremiumCache(userId, environment, {
    subscription,
    tier: isActive(subscription) ? (subscription.price_id.startsWith("pro_") ? "pro" : "premium") : "free",
    source: isActive(subscription) ? "stripe" : "none",
  });
}

/** Drops any cached plan state so the next read hits the database. */
export function clearPremiumCache(userId: string, environment: string) {
  const key = getPremiumCacheKey(userId, environment);
  premiumMemoryCache.delete(key);
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  } catch {
    // storage is best-effort only
  }
}


function subscribePremiumCache(userId: string, environment: string, listener: (cache: PremiumCache) => void) {
  const key = getPremiumCacheKey(userId, environment);
  const listeners = premiumListeners.get(key) ?? new Set<(cache: PremiumCache) => void>();
  listeners.add(listener);
  premiumListeners.set(key, listeners);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) premiumListeners.delete(key);
  };
}

function isActive(sub: SubscriptionRow | null): boolean {
  if (!sub) return false;
  const end = sub.current_period_end ? new Date(sub.current_period_end).getTime() : null;
  const future = end == null || end > Date.now();
  if (["active", "trialing", "past_due"].includes(sub.status) && future) return true;
  if (sub.status === "canceled" && end != null && end > Date.now()) return true;
  return false;
}

function fetchPremiumStatus(userId: string, environment: string) {
  const key = getPremiumCacheKey(userId, environment);
  const existing = premiumInflight.get(key);
  if (existing) return existing;

  const request = (async () => {
    const subscriptionPromise = Promise.resolve(
      supabase
        .from("subscriptions")
        .select("id,status,price_id,current_period_end,cancel_at_period_end,cancel_at,environment")
        .eq("user_id", userId)
        .eq("environment", environment)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    );

    const entitlementPromise = getMyEffectiveEntitlement().catch(() => null);

    const subResult = await Promise.race([
      subscriptionPromise,
      new Promise<"timeout">((resolve) => setTimeout(() => resolve("timeout"), PREMIUM_NETWORK_TIMEOUT_MS)),
    ]);
    if (subResult === "timeout") throw new Error("premium_timeout");
    const nextSubscription = subResult.error ? null : ((subResult.data as SubscriptionRow | null) ?? null);

    // Atualiza logo pelo registo da subscrição para a página não ficar presa,
    // mas NUNCA despromove um plano já conhecido: escrever "free" aqui fazia
    // o Premium piscar antes de a resposta autoritativa chegar.
    const previous = readPremiumCache(userId, environment);
    const subscriptionTier: PlanTier = isActive(nextSubscription)
      ? nextSubscription?.price_id.startsWith("pro_")
        ? "pro"
        : "premium"
      : "free";
    const interimTier: PlanTier =
      subscriptionTier !== "free" ? subscriptionTier : (previous?.tier ?? "free");
    writePremiumCache(userId, environment, {
      subscription: nextSubscription,
      tier: interimTier,
      source:
        interimTier === "free"
          ? "none"
          : subscriptionTier !== "free"
            ? "stripe"
            : (previous?.source ?? "stripe"),
    });

    // The authoritative result includes roles, grants and subscriptions from
    // both payment environments, exactly like quotas and the Pro pipeline.
    // If it fails (network/timeout), NEVER downgrade: keep the last known tier.
    entitlementPromise.then((entitlement) => {
      if (!entitlement) {
        const keep = readPremiumCache(userId, environment);
        writePremiumCache(userId, environment, {
          subscription: nextSubscription,
          tier: keep?.tier ?? interimTier,
          source: keep?.source ?? (interimTier === "free" ? "none" : "stripe"),
        });
        return;
      }
      writePremiumCache(userId, environment, {
        subscription: nextSubscription,
        tier: entitlement.tier,
        source: entitlement.source,
      });
    });

  })().finally(() => {
    premiumInflight.delete(key);
  });

  premiumInflight.set(key, request);
  return request;
}

/**
 * Premium status hook. Considers:
 *  - active Stripe subscription (RLS-scoped, current env)
 *  - admin/owner role (via has_active_subscription RPC, SECURITY DEFINER)
 *  - non-revoked manual grant
 */
export function usePremium() {
  const { enabled: demoMode } = useDemoMode();
  const { user, isAuthenticated } = useAuth();
  const environment = getStripeEnvironment();
  const initialCache = user ? readPremiumCache(user.id, environment) : null;
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(() => initialCache?.subscription ?? null);
  const [tier, setTier] = useState<PlanTier>(() => initialCache?.tier ?? "free");
  const [source, setSource] = useState<EntitlementSource>(() => initialCache?.source ?? "none");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setTier("free");
      setSource("none");
      setLoading(false);
      return;
    }
    const cached = readPremiumCache(user.id, environment);
    if (cached) {
      setSubscription(cached.subscription);
      setTier(cached.tier);
      setSource(cached.source);
      setLoading(false);
    }
  }, [environment, user]);

  const refetch = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setTier("free");
      setSource("none");
      setLoading(false);
      return;
    }
    const cached = readPremiumCache(user.id, environment);
    if (cached) {
      setSubscription(cached.subscription);
      setTier(cached.tier);
      setSource(cached.source);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const uiTimer = window.setTimeout(() => setLoading(false), PREMIUM_UI_LOADING_MS);
    const timeout = new Promise<"timeout">((resolve) =>
      setTimeout(() => resolve("timeout"), PREMIUM_NETWORK_TIMEOUT_MS),
    );
    try {
      const result = await Promise.race([fetchPremiumStatus(user.id, environment), timeout]);
      const latest = readPremiumCache(user.id, environment);
      if (latest) {
        setSubscription(latest.subscription);
        setTier(latest.tier);
        setSource(latest.source);
      } else if (result === "timeout") {
        if (!cached) {
          setSubscription(null);
          setTier("free");
          setSource("none");
        }
      }
    } catch {
      if (!cached) {
        setSubscription(null);
        setTier("free");
        setSource("none");
      }
    } finally {
      window.clearTimeout(uiTimer);
      setLoading(false);
    }
  }, [environment, user]);

  useEffect(() => {
    if (!user) return;
    return subscribePremiumCache(user.id, environment, (cache) => {
      setSubscription(cache.subscription);
      setTier(cache.tier);
      setSource(cache.source);
      setLoading(false);
    });
  }, [environment, user]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subscriptions:${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => { refetch(); },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "manual_premium_grants", filter: `user_id=eq.${user.id}` },
        () => { refetch(); },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_roles", filter: `user_id=eq.${user.id}` },
        () => { refetch(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, refetch]);

  // Revocations must not depend solely on database realtime publication.
  // Revalidate on focus/visibility and periodically while the tab is open.
  useEffect(() => {
    if (!user) return;
    const onFocus = () => { void refetch(); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(() => { void refetch(); }, 30_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [user, refetch]);

  // Modo de teste: o dispositivo é tratado como Pro só para validação local.
  // Nada é escrito na base de dados e nenhuma subscrição real é alterada.
  const effectiveTier: PlanTier = demoMode ? "pro" : tier;

  return {
    isPremium: effectiveTier === "premium" || effectiveTier === "pro",
    tier: effectiveTier,
    source: demoMode ? ("demo" as EntitlementSource) : source,
    isAuthenticated,
    subscription,
    loading,
    refetch,
    activate: refetch,
    deactivate: refetch,
  };
}
